/**
 * Blocked property and the reports owed to OFAC (31 CFR 501.603, 501.604; S-33): what each report is,
 * whether it is late, and whether a form is complete enough to send. The server decides the rest (who
 * may act, that a release takes two people, that the first report of a block is always required).
 */
import { isMfaCode } from './mfaApproval';
import type { BlockedProperty, EvidenceKind, ReportKind, ReportTask } from '../services/ofacBlocking.service';
import { formatCents } from './amount';

const REFERENCE_MIN = 3;
const REASON_MIN = 10;
const CURRENCY_LENGTH = 3;

export type TaskState = 'overdue' | 'due_today' | 'open' | 'filed' | 'not_required';

export function kindLabel(kind: ReportKind): string {
  switch (kind) {
    case 'blocked_initial':
      return 'Blocked property report';
    case 'annual':
      return 'Annual report of blocked property';
    case 'unblocked':
      return 'Unblocking report';
    case 'rejected':
      return 'Rejected transaction report';
  }
}

export function taskState(task: Pick<ReportTask, 'outcome' | 'days_left'>): TaskState {
  if (task.outcome !== null) return task.outcome;
  if (task.days_left < 0) return 'overdue';
  return task.days_left === 0 ? 'due_today' : 'open';
}

/** A report is late the day after it is due. */
export function isLate(task: Pick<ReportTask, 'outcome' | 'days_left'>): boolean {
  return task.outcome === null && task.days_left < 0;
}

export function canFile(input: { reference: string; file: File | null; code: string; date: string }): boolean {
  return (
    input.reference.trim().length >= REFERENCE_MIN &&
    input.file !== null &&
    input.date !== '' &&
    isMfaCode(input.code.trim())
  );
}

/** The placement of blocked cash: either the date, the blocked account, the bank reference, the amount and its proof, or the reason there is no cash. */
export function canPlace(input: {
  noCash: boolean;
  date: string;
  account: string;
  bank: string;
  amount: string;
  currency: string;
  file: File | null;
  reason: string;
  code: string;
}): boolean {
  const detail = input.noCash
    ? input.reason.trim().length >= REASON_MIN
    : input.date !== '' &&
      input.account.trim().length >= REFERENCE_MIN &&
      input.bank.trim().length >= REFERENCE_MIN &&
      Number(input.amount) > 0 &&
      input.currency.trim().length === CURRENCY_LENGTH &&
      input.file !== null;
  return detail && isMfaCode(input.code.trim());
}

export function canRecordEvidence(input: { reference: string; reason: string; code: string }): boolean {
  return (
    input.reference.trim().length >= REFERENCE_MIN &&
    input.reason.trim().length >= REASON_MIN &&
    isMfaCode(input.code.trim())
  );
}

export function canRecordRejected(input: {
  entity: string;
  authority: string;
  description: string;
  date: string;
  code: string;
}): boolean {
  return (
    input.entity.trim() !== '' &&
    input.authority.trim().length >= REFERENCE_MIN &&
    input.description.trim().length >= REASON_MIN &&
    input.date !== '' &&
    isMfaCode(input.code.trim())
  );
}

/** What is held, one currency per part. */
export function heldLine(held: ReadonlyArray<{ currency: string; amount: string }>): string {
  return held
    .map((h) => `${h.currency} ${formatCents(h.amount)}`)
    .join(' · ');
}

/** Who the blocked property is about and where it is held, in plain words: the client for a holding, the
 *  sender the bank named for a transfer; the entity by name. Never a bare dash. */
export function blockSubject(
  block: Pick<BlockedProperty, 'subject_kind' | 'client_name' | 'sender_name' | 'entity_name'>,
): { who: string; where: string } {
  const unknown = block.subject_kind === 'position' ? 'A client' : 'A sender the bank did not name';
  return {
    who: block.client_name ?? block.sender_name ?? unknown,
    where: block.entity_name ?? 'A US entity of the group',
  };
}

/** A block still in force, or a confirmed match with nothing blocked (always in force while listed), whose
 *  person the latest list no longer names, with nothing recorded yet: the officer has a delisting to verify.
 *  The list alone never releases anything. */
export function hasDelistingToVerify(
  item: Readonly<{ in_force?: boolean; listing_ended: boolean; pending_request_id: string | null }>,
): boolean {
  return (item.in_force ?? true) && item.listing_ended && item.pending_request_id === null;
}

export function evidenceLabel(kind: EvidenceKind): string {
  return kind === 'ofac_license' ? 'An OFAC license' : 'A delisting';
}
